import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaSearch } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import dnPopup from "./utils/dnPopup";
import logo_eagle from "./assets/img/logo_eagle.png";
import ScrollSelect from "./ScrollSelect";
import "./Shift.css";

// "14:30" / "14:30:00" -> "2:30 PM"
const to12h = (t) => {
    if (!t) return "";
    const [h, m] = String(t).split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
};

// "General Shift (10:00 AM - 6:00 PM)"
const shiftLabel = (s) => `${s.shift_name} (${to12h(s.start_time)} - ${to12h(s.end_time)})`;

const Assign_Shifts = ({ navSize }) => {
    const [shifts, setShifts] = useState([]);
    const [emps, setEmps] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selected, setSelected] = useState(() => new Set());
    const [targetShift, setTargetShift] = useState("");
    const [saving, setSaving] = useState(false);

    // filters
    const [search, setSearch] = useState("");
    const [deptFilter, setDeptFilter] = useState("");
    const [locFilter, setLocFilter] = useState("");
    const [shiftFilter, setShiftFilter] = useState(""); // "", "none", or a shift_id

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [sRes, aRes] = await Promise.all([
                apiFetch("/api/shift/list", { credentials: "include" }),
                apiFetch("/api/shift/allocations", { credentials: "include" }),
            ]);
            const sData = await sRes.json();
            const aData = await aRes.json();
            setShifts(Array.isArray(sData) ? sData : []);
            setEmps(Array.isArray(aData) ? aData : []);
        } catch (err) {
            console.error("Error loading allocations:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const departments = useMemo(
        () => [...new Set(emps.map((e) => e.department_name).filter(Boolean))].sort(),
        [emps]
    );
    const locations = useMemo(
        () => [...new Set(emps.map((e) => e.emp_location).filter(Boolean))].sort(),
        [emps]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return emps.filter((e) => {
            if (deptFilter && e.department_name !== deptFilter) return false;
            if (locFilter && e.emp_location !== locFilter) return false;
            if (shiftFilter === "none" && e.shift_id) return false;
            if (shiftFilter && shiftFilter !== "none" && String(e.shift_id) !== shiftFilter) return false;
            if (q && !`${e.emp_name || ""} ${e.emp_id || ""} ${e.department_name || ""}`.toLowerCase().includes(q))
                return false;
            return true;
        });
    }, [emps, search, deptFilter, locFilter, shiftFilter]);

    const toggle = (emp_id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(emp_id) ? next.delete(emp_id) : next.add(emp_id);
            return next;
        });
    };

    const allFilteredSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.emp_id));
    const toggleAll = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) filtered.forEach((e) => next.delete(e.emp_id));
            else filtered.forEach((e) => next.add(e.emp_id));
            return next;
        });
    };

    const assign = async () => {
        if (selected.size === 0) return dnPopup.warning("No employees", "Select at least one employee.");
        if (!targetShift) return dnPopup.warning("No shift", "Choose a shift to assign.");
        const shiftName = shifts.find((s) => String(s.shift_id) === String(targetShift))?.shift_name || "";
        setSaving(true);
        try {
            const res = await apiFetch("/api/shift/allocate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ emp_ids: [...selected], shift_id: Number(targetShift) }),
                credentials: "include",
            });
            const data = await res.json();
            if (data.success) {
                dnPopup.success("Shift Assigned", `${data.count} employee(s) assigned to "${shiftName}".`);
                setSelected(new Set());
                load();
            } else {
                dnPopup.error("Failed", data.error || "Something went wrong.");
            }
        } catch (err) {
            console.error("Assign error:", err);
            dnPopup.error("Error", "Failed to assign shift.");
        } finally {
            setSaving(false);
        }
    };

    const unassign = async () => {
        if (selected.size === 0) return dnPopup.warning("No employees", "Select at least one employee.");
        const ok = await dnPopup.confirm({
            title: "Remove shift?",
            message: `Remove the shift from ${selected.size} employee(s)? They will be locked out until reassigned.`,
            confirmLabel: "Remove",
            danger: true,
        });
        if (!ok) return;
        setSaving(true);
        try {
            const res = await apiFetch("/api/shift/unallocate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ emp_ids: [...selected] }),
                credentials: "include",
            });
            const data = await res.json();
            if (data.success) {
                dnPopup.success("Removed", `Shift removed from ${data.count} employee(s).`);
                setSelected(new Set());
                load();
            } else {
                dnPopup.error("Failed", data.error || "Something went wrong.");
            }
        } catch (err) {
            console.error("Unassign error:", err);
            dnPopup.error("Error", "Failed to remove shift.");
        } finally {
            setSaving(false);
        }
    };

    const unassignedCount = emps.filter((e) => !e.shift_id).length;

    return (
        <div className="shift-container">
            <div className={`shift-dashboard ${navSize}`}>
                <div className="shift-header">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dTime Logo" className="page-logo" />
                        </a>
                        Assign Shifts
                    </h2>
                    <p className="shift-subtitle">
                        Assign a shift to employees. Filter by department or location for bulk allocation,
                        tick the employees, pick a shift and assign.
                        {unassignedCount > 0 && (
                            <span className="shift-unassigned-note"> {unassignedCount} employee(s) have no shift yet.</span>
                        )}
                    </p>
                </div>

                {/* Filters */}
                <div className="shift-alloc-toolbar">
                    <div className="shift-search">
                        <FaSearch className="shift-search-icon" />
                        <input
                            type="text"
                            placeholder="Search name, employee ID, department…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
                        <option value="">All Departments</option>
                        {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select value={locFilter} onChange={(e) => setLocFilter(e.target.value)}>
                        <option value="">All Locations</option>
                        {locations.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <select value={shiftFilter} onChange={(e) => setShiftFilter(e.target.value)}>
                        <option value="">Any Shift Status</option>
                        <option value="none">No shift assigned</option>
                        {shifts.map((s) => <option key={s.shift_id} value={s.shift_id}>{s.shift_name}</option>)}
                    </select>
                </div>

                {/* Employee table */}
                <div className="shift-card shift-alloc-card">
                    {loading ? (
                        <div className="shift-empty">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="shift-empty">No employees match the current filters.</div>
                    ) : (
                        <table className="shift-table">
                            <thead>
                                <tr>
                                    <th className="shift-check-col">
                                        <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} />
                                    </th>
                                    <th>Employee</th>
                                    <th>Employee ID</th>
                                    <th>Department</th>
                                    <th>Location</th>
                                    <th>Current Shift</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((e) => (
                                    <tr
                                        key={e.emp_id}
                                        className={selected.has(e.emp_id) ? "shift-row-selected" : ""}
                                        onClick={() => toggle(e.emp_id)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td className="shift-check-col">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(e.emp_id)}
                                                readOnly
                                            />
                                        </td>
                                        <td className="shift-name-cell">{e.emp_name || e.emp_id}</td>
                                        <td><span className="shift-idchip">{e.emp_id}</span></td>
                                        <td>{e.department_name || "—"}</td>
                                        <td>{e.emp_location || "—"}</td>
                                        <td>
                                            {e.shift_id ? (
                                                <span className="shift-assigned-pill">{e.shift_name}</span>
                                            ) : (
                                                <span className="shift-none-pill">No shift</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Action bar */}
                <div className="shift-alloc-actionbar">
                    <span className="shift-selected-count">{selected.size} selected</span>
                    <ScrollSelect
                        value={targetShift}
                        options={shifts.map((s) => ({ value: s.shift_id, label: shiftLabel(s) }))}
                        placeholder="Select a shift…"
                        minWidth={300}
                        align="left"
                        onChange={(v) => setTargetShift(v)}
                    />
                    <button className="shift-save-btn" onClick={assign} disabled={saving}>
                        {saving ? "Saving…" : "Assign to Shift"}
                    </button>
                    <button className="shift-cancel-btn" onClick={unassign} disabled={saving}>
                        Remove Shift
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Assign_Shifts;
